import { createStyles } from "@mantine/emotion";

export const useStyles = createStyles(() => ({
  mainContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "max-content",
    // Matches `hero`'s background, not the sidebar's white — this sits behind
    // the sidebar as a plain (unrounded) rectangle, so if it were white it
    // would peek through the sidebar's rounded corner cutouts as a mismatched
    // square patch against the grey page.
    margin: 0,
    backgroundColor: "#F9F9FA",
  },

  // `right: 0` instead of a 100vw-based width: the old calc left ~60px of dead
  // space on the right and was scrollbar-unaware (100vw includes the scrollbar).
  // Anchoring to the containing block fills the remaining width exactly.
  //
  // `absolute`, not `fixed`: a fixed box taller than the viewport is a classic
  // CSS trap — the document reports extra scrollable height, but a fixed
  // element never actually translates on scroll, so content past the first
  // viewport becomes permanently unreachable. `absolute` against
  // `mainContainer` (position: relative) behaves like ordinary flow content
  // for scrolling purposes — the page/document scrolls to reveal all of it —
  // while NavBar (still `fixed`) stays pinned as a persistent sidebar
  // throughout. See index.css for the horizontal-scroll lockdown this
  // requires (NavBar staying fixed while hero pans would otherwise let a
  // horizontal swipe drag hero out from under the sidebar).
  hero: {
    position: "absolute",
    top: 0,
    left: 240,
    right: 0,
    // No `bottom` set, so the box only shrink-wraps its content — on a page
    // shorter than the viewport (e.g. Withdrawal) the background stopped
    // right after the content instead of covering the rest of the screen.
    minHeight: "100vh",
    padding: "24px 32px 24px 0px",
    margin: "0px 0px 0px 20px",
    backgroundColor: "#F9F9FA",
  },

  // Mobile
  mobileContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    // Matches the desktop page background (`hero`) — the navbar equivalent
    // here, `mobileTopBar`, keeps its own white colour unchanged.
    backgroundColor: "#F9F9FA",
  },

  mobileTopBar: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    // Rigid 56px bar. In the flex column, a bare `height` is only a hint the
    // flex algorithm can override — when <main> reflows (or the page is
    // zoomed) the bar stretched to fill the cross space. flexShrink:0 + a
    // min/max height lock it so it can never grow or collapse.
    flex: "0 0 56px",
    minHeight: 56,
    maxHeight: 56,
    padding: "0 16px",
    backgroundColor: "#FFFFFF",
    borderBottom: "1px solid #edf0f2",
    color: "#182230",
  },

  // Centred independent of the burger's width, so it sits in the true middle
  // of the bar rather than the middle of the space left after the icon.
  mobileLogo: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    height: 24,
  },

  mobileHero: {
    flex: 1,
    width: "100%",
    padding: "16px",
  },

  // Flush against the left, top, and bottom edges (it's already the
  // leftmost/full-height thing on screen — margin there only added dead
  // space against the viewport edge) with a floating-card curve on the
  // right side only, where it meets the dimmed page content.
  drawerContent: {
    margin: "0 16px 0 0",
    // `100vh` on mobile browsers is the *largest* possible viewport (chrome
    // hidden) — with the address bar actually showing, the real visible
    // area is shorter, so a fixed 100vh box ran past the bottom of the
    // screen and clipped the footer (name + Logout) below the fold. `dvh`
    // tracks the browser's actual visible viewport instead.
    height: "100dvh",
    borderRadius: "0 20px 20px 0",
    overflow: "hidden",
    boxShadow: "0 12px 32px rgba(16, 24, 40, 0.18)",
  },

  // Flex column so `drawerHeader` (44px) and the embedded NavBar share this
  // box's actual height instead of each independently claiming 100% of it —
  // that double-counted the header, pushing NavBar's flex:1-positioned
  // footer (name + Logout) 44px past the drawer's visible bottom edge,
  // clipped by drawerContent's overflow:hidden instead of sitting flush.
  drawerBody: {
    padding: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },

  // Plain white header strip for the close button — no colour break from the
  // NavBar body beneath it, so the drawer reads as one continuous white card
  // (logo included) instead of a dark banner bolted onto a white panel.
  drawerHeader: {
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "8px 12px 0",
    backgroundColor: "#FFFFFF",
  },
}));
